
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MetricCardProps {
  title: string;
  value: number | string;
  icon: LucideIcon;
  iconColor?: string;
  description?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  icon: Icon,
  iconColor = "primary",
  description,
  trend,
  trendValue
}) => {
  const colorMap: Record<string, string> = {
    'primary': 'bg-gradient-to-br from-primary/10 to-primary/5 text-primary',
    'blue-500': 'bg-gradient-to-br from-blue-50 to-blue-100 text-blue-600 dark:from-blue-900/20 dark:to-blue-800/20',
    'yellow-500': 'bg-gradient-to-br from-yellow-50 to-yellow-100 text-yellow-600 dark:from-yellow-900/20 dark:to-yellow-800/20',
    'green-500': 'bg-gradient-to-br from-green-50 to-green-100 text-green-600 dark:from-green-900/20 dark:to-green-800/20',
    'purple-500': 'bg-gradient-to-br from-purple-50 to-purple-100 text-purple-600 dark:from-purple-900/20 dark:to-purple-800/20',
    'orange-500': 'bg-gradient-to-br from-orange-50 to-orange-100 text-orange-600 dark:from-orange-900/20 dark:to-orange-800/20',
  };

  const trendColors = {
    up: 'text-green-600',
    down: 'text-red-600',
    neutral: 'text-muted-foreground',
  };
  
  return (
    <div className="flex items-start justify-between group">
      <div className="space-y-1.5 flex-1 min-w-0">
        <p className="text-xs sm:text-sm font-medium text-muted-foreground">{title}</p>
        <div className="flex items-baseline gap-2">
          <p className="text-2xl sm:text-3xl font-bold tracking-tight">{value}</p>
          {trend && trendValue && (
            <span className={cn("text-xs font-medium", trendColors[trend])}>
              {trendValue}
            </span>
          )}
        </div>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      <div className={cn(
        "p-2.5 sm:p-3 rounded-xl transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg flex-shrink-0",
        colorMap[iconColor] || colorMap.primary
      )}>
        <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
      </div>
    </div>
  );
};

export default MetricCard;
